import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HardHat, Coins, Wallet } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { REVENUE_FIELDS, INVESTMENT_FIELDS, fmt, tooltipStyle } from "./oversiktConstants";
import { num } from "@/lib/formatters";

const OversiktCompareCharts = ({ oversiktData, colors }) => {
  const entries = Object.entries(oversiktData || {});
  if (entries.length === 0) return null;

  const years = new Set();
  entries.forEach(([, k]) => k.data?.forEach((d) => years.add(d.year)));
  const investPerCapita = Array.from(years).sort().map((year) => {
    const point = { year };
    entries.forEach(([, k]) => {
      const d = k.data?.find((x) => x.year === year);
      if (d?.investment_per_capita?.sum_investeringsutgifter != null) {
        point[k.name] = d.investment_per_capita.sum_investeringsutgifter;
      }
    });
    return point;
  });

  const revenueShare = entries.map(([, k]) => {
    const latest = [...(k.data || [])].reverse().find((d) => d.revenue?.sum_driftsinntekter);
    const point = { name: k.name, year: latest?.year };
    const total = latest?.revenue.sum_driftsinntekter || 0;
    REVENUE_FIELDS.forEach((f) => {
      point[f.label] = total ? +(((latest.revenue[f.key] || 0) / total) * 100).toFixed(1) : 0;
    });
    return point;
  }).filter((p) => p.year);

  const latestExpenses = entries.map(([, k]) => [k.name, [...(k.data || [])].reverse().find((d) => d.expenses_per_capita)]);
  const expensesYear = latestExpenses.find(([, d]) => d)?.[1]?.year;
  const expensesPerCapita = INVESTMENT_FIELDS.map((f) => {
    const point = { name: f.label, _total: 0 };
    latestExpenses.forEach(([name, d]) => {
      if (d) {
        point[name] = d.expenses_per_capita[f.key] ?? 0;
        point._total += Math.abs(point[name]);
      }
    });
    return point;
  }).sort((a, b) => b._total - a._total);

  return (
    <>
      <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-investment-chart">
        <CardHeader className="border-b border-[#E6E9F0] pb-4">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <HardHat className="w-5 h-5 text-[#103766]" />
            Brutto investeringsutgifter pr innbyggjar (kr)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={investPerCapita}>
                {endringsLinjer(investPerCapita)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
                <Legend />
                {entries.map(([code, k], idx) => (
                  <Line key={code} type="monotone" dataKey={k.name} stroke={colors[idx % colors.length]} strokeWidth={2} dot={{ fill: colors[idx % colors.length] }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-[#8AA4BF] mt-4 text-center">Kjelde: SSB 12362 (brutto investeringsutgifter per tenesteområde)</p>
        </CardContent>
      </Card>

      <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-revenue-chart">
        <CardHeader className="border-b border-[#E6E9F0] pb-4">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#103766]" />
            Inntektskjelder, andel av driftsinntekter (%)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueShare} layout="vertical" stackOffset="expand">
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis type="number" stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${Math.round(v * 100)} %`} />
                <YAxis type="category" dataKey="name" stroke="#8AA4BF" fontSize={12} width={150} />
                <Tooltip
                  formatter={(v, name) => [`${num(v)} %`, name]}
                  labelFormatter={(label, payload) => `${label} (${payload?.[0]?.payload?.year ?? ""})`}
                  contentStyle={tooltipStyle}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {REVENUE_FIELDS.map((f) => (
                  <Bar key={f.key} dataKey={f.label} stackId="rev" fill={f.color} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-[#8AA4BF] mt-4 text-center">Kjelde: SSB 13551 – siste tilgjengelege år</p>
        </CardContent>
      </Card>

      {expensesYear && (
        <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-expenses-chart">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#103766]" />
              Netto driftsutgifter pr innbyggjar per tenesteområde (kr, {expensesYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="h-[680px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={expensesPerCapita} layout="vertical" barGap={1} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis type="number" stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" stroke="#8AA4BF" fontSize={12} width={230} interval={0} />
                  <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
                  <Legend />
                  {entries.map(([code, k], idx) => (
                    <Bar key={code} dataKey={k.name} fill={colors[idx % colors.length]} radius={[0, 4, 4, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-[#8AA4BF] mt-4 text-center">Kjelde: SSB 12362 (netto driftsutgifter per innbyggjar). Negative verdiar = området gir netto inntekt.</p>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default OversiktCompareCharts;
